import {
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Layout,
  Select,
  Skeleton,
  Statistic,
  Table,
  Tag,
  notification,
  theme,
  Row,
  Col,
  Switch,
  Tooltip,
} from "antd";
import { Content } from "antd/es/layout/layout";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiSubscriptionCreate, apiSubscriptionDetail, apiSubscriptionUpdate, apiSubscribersList } from "../../../api/subscription";
import InputMoney from "../../common/InputMoney/InputMoney";
import { userHasPermission } from "../../../helpers/user";
import {
  QuestionCircleOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  SyncOutlined,
  EditOutlined,
  TeamOutlined,
  SearchOutlined,
  EyeOutlined,
  CopyOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { formatDate, formatPhoneWithCountry } from "../../../utils/format";
import dayjs from "dayjs";
import "./SubscriptionCreate.scss";

const { Option } = Select;
const { TextArea } = Input;

const frequencyOptions = [
  { value: "weekly", label: "Semanal" },
  { value: "biweekly", label: "Quinzenal" },
  { value: "monthly", label: "Mensal" },
  { value: "bimonthly", label: "Bimestral" },
  { value: "quarterly", label: "Trimestral" },
  { value: "semiannual", label: "Semestral" },
  { value: "annual", label: "Anual" },
];

const paymentMethodOptions = [
  { value: "PIX", label: "PIX" },
  { value: "BOLETO", label: "Boleto" },
  { value: "CREDIT_CARD", label: "Cartão de Crédito" },
];

const SubscriptionCreate = () => {
  const { id } = useParams();
  const isViewMode = !!id;

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isViewMode);
  const [isEditMode, setIsEditMode] = useState(false);

  // Plan info
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState({ price: 0 });
  const [frequency, setFrequency] = useState("monthly");
  const [endDate, setEndDate] = useState(null);

  // Payment methods
  const [paymentMethods, setPaymentMethods] = useState(["PIX"]);
  const [paymentMethodsNoTax, setPaymentMethodsNoTax] = useState([]);

  // Notification config
  const [notifyNewPaymentLink, setNotifyNewPaymentLink] = useState(true);
  const [daysBeforeDue, setDaysBeforeDue] = useState(5);
  const [notifyOnDueDate, setNotifyOnDueDate] = useState(true);
  const [daysAfterOverdue, setDaysAfterOverdue] = useState(3);
  const [notifyPaymentConfirmed, setNotifyPaymentConfirmed] = useState(true);

  // Metrics (view mode only)
  const [metrics, setMetrics] = useState(null);

  // Subscribers (view mode only)
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [subscribersSearch, setSubscribersSearch] = useState("");
  const [subscribersPagination, setSubscribersPagination] = useState({
    current: 1,
    total: 0,
    pageSize: 10,
  });

  useEffect(() => {
    if (isViewMode) {
      fetchSubscriptionDetails();
      fetchSubscribers({ page: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSubscribers = async (params = {}) => {
    setSubscribersLoading(true);
    try {
      const filters = {
        page: params.page || 1,
        ...(subscribersSearch && { search: subscribersSearch }),
      };
      const data = await apiSubscribersList(id, filters);
      setSubscribers(data.results || data || []);
      setSubscribersPagination((prev) => ({
        ...prev,
        current: params.page || 1,
        total: data.count || (Array.isArray(data) ? data.length : 0),
      }));
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao carregar os assinantes.",
        placement: "topRight",
      });
    } finally {
      setSubscribersLoading(false);
    }
  };

  const handleSubscribersSearch = () => {
    fetchSubscribers({ page: 1 });
  };

  const handleSubscribersTableChange = (pagination) => {
    fetchSubscribers({ page: pagination.current });
  };

  const fetchSubscriptionDetails = async () => {
    setPageLoading(true);
    try {
      const data = await apiSubscriptionDetail(id);

      // Set plan info
      setName(data.name);
      setDescription(data.description);
      setAmount({ price: parseFloat(data.amount) });
      setFrequency(data.frequency);
      setEndDate(data.end_date ? dayjs(data.end_date) : null);

      // Set payment methods
      setPaymentMethods(data.payment_methods || []);
      setPaymentMethodsNoTax(data.payment_methods_no_tax || []);

      // Set notification config
      if (data.notification_config) {
        setNotifyNewPaymentLink(data.notification_config.notify_new_payment_link);
        setDaysBeforeDue(data.notification_config.days_before_due);
        setNotifyOnDueDate(data.notification_config.notify_on_due_date);
        setDaysAfterOverdue(data.notification_config.days_after_overdue);
        setNotifyPaymentConfirmed(data.notification_config.notify_payment_confirmed);
      }

      // Set metrics
      setMetrics(data.metrics);

      // Update form values
      form.setFieldsValue({
        name: data.name,
        description: data.description,
        amount: data.amount,
        frequency: data.frequency,
        endDate: data.end_date ? dayjs(data.end_date) : null,
      });
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao carregar os detalhes da assinatura.",
        placement: "topRight",
      });
      navigate("/assinaturas");
    } finally {
      setPageLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      if (amount.price === 0) {
        notification.error({
          message: "Erro",
          description: "Preencha o campo de valor!",
          placement: "topRight",
        });
        return;
      }

      if (paymentMethods.length === 0) {
        notification.error({
          message: "Erro",
          description: "Selecione pelo menos um metodo de pagamento!",
          placement: "topRight",
        });
        return;
      }

      const jsonData = {
        name,
        description,
        amount: amount.price.toFixed(2),
        payment_methods: paymentMethods,
        payment_methods_no_tax: paymentMethodsNoTax,
        end_date: endDate ? endDate.toISOString().split("T")[0] : null,
        frequency,
        notification_config: {
          notify_new_payment_link: notifyNewPaymentLink,
          days_before_due: daysBeforeDue,
          notify_on_due_date: notifyOnDueDate,
          days_after_overdue: daysAfterOverdue,
          notify_payment_confirmed: notifyPaymentConfirmed,
        },
      };

      if (isEditMode) {
        await apiSubscriptionUpdate(id, jsonData);
        notification.success({
          message: "Sucesso",
          description: "Assinatura atualizada com sucesso!",
          placement: "topRight",
        });
        setIsEditMode(false);
      } else {
        await apiSubscriptionCreate(jsonData);
        notification.success({
          message: "Sucesso",
          description: "Assinatura criada com sucesso!",
          placement: "topRight",
        });
        navigate("/assinaturas");
      }
    } catch (error) {
      const action = isEditMode ? "atualizar" : "criar";
      if (error.status === 400) {
        notification.error({
          message: "Erro",
          description: `Ocorreu um erro ao ${action} a assinatura! Verifique se todos os campos estao preenchidos corretamente.`,
          placement: "topRight",
        });
      } else if (error.status === 500) {
        notification.error({
          message: "Erro",
          description:
            "Ocorreu um erro no servidor. Por favor, tente novamente mais tarde.",
          placement: "topRight",
        });
      } else {
        notification.error({
          message: "Erro",
          description:
            "Ocorreu um erro ao processar a requisicao. Por favor, tente novamente.",
          placement: "topRight",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const onChangeInput = (event, nameObject, isNumber) => {
    setAmount({
      [nameObject]: isNumber ? Number(event.target.value) : event.target.value,
    });
  };

  const onFinish = () => {
    form
      .validateFields()
      .then(() => {
        handleSave();
      })
      .catch(() => {
        notification.error({
          message: "Erro",
          description: "Preencha todos os campos obrigatorios.",
          placement: "topRight",
        });
      });
  };

  const handlePaymentMethodChange = (method, checked) => {
    if (isViewMode && !isEditMode) return;
    if (checked) {
      setPaymentMethods([...paymentMethods, method]);
    } else {
      setPaymentMethods(paymentMethods.filter((m) => m !== method));
      setPaymentMethodsNoTax(paymentMethodsNoTax.filter((m) => m !== method));
    }
  };

  const handlePaymentMethodNoTaxChange = (method, checked) => {
    if (isViewMode && !isEditMode) return;
    if (checked) {
      setPaymentMethodsNoTax([...paymentMethodsNoTax, method]);
    } else {
      setPaymentMethodsNoTax(paymentMethodsNoTax.filter((m) => m !== method));
    }
  };

  const subscriptionLink = `${window.location.origin}/assinar/${id}`;

  const handleCopySubscriptionLink = async () => {
    try {
      await navigator.clipboard.writeText(subscriptionLink);
      notification.success({
        message: "Sucesso",
        description: "Link copiado para a área de transferência!",
        placement: "topRight",
      });
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Não foi possível copiar o link.",
        placement: "topRight",
      });
    }
  };

  const renderMetrics = () => {
    if (!metrics) return null;

    const metricsConfig = [
      {
        title: "Total de Assinantes",
        value: metrics.total_subscribers,
        icon: <UserOutlined />,
        color: "#1890ff",
      },
      {
        title: "Ativos",
        value: metrics.active_subscribers,
        icon: <CheckCircleOutlined />,
        color: "#52c41a",
      },
      {
        title: "Pagos",
        value: metrics.paid_subscribers,
        icon: <CheckCircleOutlined />,
        color: "#13c2c2",
      },
      {
        title: "Pendentes",
        value: metrics.pending_subscribers,
        icon: <ClockCircleOutlined />,
        color: "#faad14",
      },
      {
        title: "Em Atraso",
        value: metrics.overdue_subscribers,
        icon: <ExclamationCircleOutlined />,
        color: "#fa8c16",
      },
      {
        title: "Cancelados",
        value: metrics.canceled_subscribers,
        icon: <StopOutlined />,
        color: "#ff4d4f",
      },
      {
        title: "Falhos",
        value: metrics.failed_subscribers,
        icon: <CloseCircleOutlined />,
        color: "#f5222d",
      },
    ];

    return (
      <Content
        style={{
          padding: 24,
          margin: "30px 0 0 0",
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <div className="container-subtitle">
          <h3 className="subtitle-page">
            <SyncOutlined style={{ marginRight: 8 }} />
            Metricas da Assinatura
          </h3>
        </div>
        <Divider />
        <Row gutter={[16, 16]}>
          {metricsConfig.map((metric, index) => (
            <Col xs={12} sm={8} md={6} lg={3} key={index}>
              <Card className="metric-card" style={{ borderTop: `3px solid ${metric.color}` }}>
                <Statistic
                  title={
                    <span className="metric-title">
                      {React.cloneElement(metric.icon, { style: { color: metric.color, marginRight: 4 } })}
                      {metric.title}
                    </span>
                  }
                  value={metric.value}
                  valueStyle={{ color: metric.color, fontSize: 28 }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      </Content>
    );
  };

  const getSubscriberStatusTag = (status) => {
    const statusConfig = {
      paid: { color: "green", label: "Pago" },
      pending: { color: "orange", label: "Pendente" },
      overdue: { color: "red", label: "Em Atraso" },
      canceled: { color: "default", label: "Cancelado" },
      failed: { color: "error", label: "Falho" },
    };
    const config = statusConfig[status] || { color: "default", label: status };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const subscribersColumns = [
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      responsive: ["md"],
    },
    {
      title: "Telefone",
      key: "phone",
      responsive: ["lg"],
      render: (_, record) =>
        record?.phone_number
          ? formatPhoneWithCountry(record?.country_code, record?.phone_number)
          : "-",
    },
    {
      title: "CPF/CNPJ",
      dataIndex: "taxpayer_id",
      key: "taxpayer_id",
      responsive: ["lg"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: getSubscriberStatusTag,
    },
    {
      title: "Próxima Cobrança",
      dataIndex: "next_billing_date",
      key: "next_billing_date",
      responsive: ["lg"],
      render: (date) => (date ? formatDate(date) : "-"),
    },
    {
      title: "Assinante desde",
      dataIndex: "subscribed_at",
      key: "subscribed_at",
      responsive: ["md"],
      render: (date) => (date ? formatDate(date) : "-"),
    },
    {
      title: "Ações",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/assinatura/${id}/assinante/${record.id}`)}
        >
          Ver
        </Button>
      ),
    },
  ];

  const renderSubscribers = () => {
    return (
      <Content
        style={{
          padding: 24,
          margin: "30px 0 0 0",
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <div className="container-subtitle">
          <h3 className="subtitle-page">
            <TeamOutlined style={{ marginRight: 8 }} />
            Assinantes
          </h3>
        </div>
        <Divider />
        <div style={{ marginBottom: 16, display: "flex", gap: 8 }}>
          <Input
            placeholder="Buscar por nome, email ou CPF/CNPJ"
            value={subscribersSearch}
            onChange={(e) => setSubscribersSearch(e.target.value)}
            onPressEnter={handleSubscribersSearch}
            style={{ maxWidth: 400 }}
            prefix={<SearchOutlined />}
          />
          <Button type="primary" onClick={handleSubscribersSearch}>
            Buscar
          </Button>
        </div>
        {subscribersLoading ? (
          <Skeleton active />
        ) : (
          <Table
            columns={subscribersColumns}
            dataSource={subscribers.map((item) => ({ ...item, key: item.id }))}
            pagination={{
              current: subscribersPagination.current,
              total: subscribersPagination.total,
              pageSize: subscribersPagination.pageSize,
              showSizeChanger: false,
            }}
            onChange={handleSubscribersTableChange}
            locale={{ emptyText: <Empty description="Nenhum assinante encontrado" /> }}
            bordered
          />
        )}
      </Content>
    );
  };

  if (pageLoading) {
    return (
      <div className="SubscriptionCreate">
        <Layout style={{ padding: "1.25rem" }}>
          <Skeleton active paragraph={{ rows: 10 }} />
        </Layout>
      </div>
    );
  }

  return (
    <div className="SubscriptionCreate">
      <Layout style={{ padding: "1.25rem" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            <h2 className="title-page">
              {isViewMode ? "Detalhes da Assinatura" : "Nova Assinatura"}
            </h2>
            {isViewMode && userHasPermission("add_subscription_plan") && (
              <Button
                type={isEditMode ? "primary" : "default"}
                icon={<EditOutlined />}
                onClick={() => setIsEditMode(!isEditMode)}
              >
                {isEditMode ? "Cancelar Edição" : "Editar"}
              </Button>
            )}
          </div>
          <Divider />
          <Breadcrumb.Item>Assinaturas</Breadcrumb.Item>
          <Breadcrumb.Item>
            {isViewMode ? "Detalhes" : "Nova Assinatura"}
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Metrics Section (View Mode Only) */}
        {isViewMode && renderMetrics()}

        {/* Subscription Link (View Mode Only) */}
        {isViewMode && !isEditMode && (
          <Content
            style={{
              padding: 24,
              margin: "30px 0 0 0",
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
          >
            <div className="container-subtitle">
              <h3 className="subtitle-page">
                <LinkOutlined style={{ marginRight: 8 }} />
                Link de Assinatura
              </h3>
            </div>
            <Divider />
            <div className="subscription-link-container">
              <p className="subscription-link-description">
                Compartilhe este link com seus clientes para que eles possam assinar o plano:
              </p>
              <div className="subscription-link-box">
                <Input
                  value={subscriptionLink}
                  readOnly
                  addonAfter={
                    <Tooltip title="Copiar link">
                      <CopyOutlined
                        onClick={handleCopySubscriptionLink}
                        style={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  }
                />
                <Button
                  type="primary"
                  icon={<CopyOutlined />}
                  onClick={handleCopySubscriptionLink}
                >
                  Copiar Link
                </Button>
              </div>
            </div>
          </Content>
        )}

        {/* Subscribers Section (View Mode Only) */}
        {isViewMode && !isEditMode && renderSubscribers()}

        {/* Section 1: Plan Info */}
        <Content
          style={{
            padding: 24,
            margin: "30px 0 0 0",
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <div className="container-subtitle">
            <h3 className="subtitle-page">
              {isViewMode ? "1." : "1."} Informações do Plano
            </h3>
          </div>
          <Divider />
          <Form form={form} layout="vertical">
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Nome da Assinatura"
                  name="name"
                  rules={[
                    { required: !isViewMode || isEditMode, message: "Por favor, insira o nome da assinatura." },
                  ]}
                >
                  <Input
                    placeholder="Ex: Assinatura Premium"
                    onChange={(e) => setName(e.target.value)}
                    value={name}
                    disabled={isViewMode && !isEditMode}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Valor"
                  name="amount"
                  initialValue={0}
                  rules={[{ required: !isViewMode || isEditMode, message: "Por favor, insira o valor." }]}
                >
                  <InputMoney
                    onChange={(e) => onChangeInput(e, "price", true)}
                    value={amount?.price || "0,00"}
                    placeholder="R$ 0,00"
                    disabled={isViewMode && !isEditMode}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Frequência de Cobrança"
                  name="frequency"
                  initialValue="monthly"
                  rules={[{ required: !isViewMode || isEditMode, message: "Selecione a frequência." }]}
                >
                  <Select
                    value={frequency}
                    onChange={(value) => setFrequency(value)}
                    placeholder="Selecione a frequência"
                    disabled={isViewMode && !isEditMode}
                  >
                    {frequencyOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <span>
                      Data de Encerramento
                      {(!isViewMode || isEditMode) && (
                        <Tooltip title="Deixe em branco para assinatura sem data de término">
                          <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                        </Tooltip>
                      )}
                    </span>
                  }
                  name="endDate"
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format={{ format: 'DD/MM/YYYY', type: 'mask' }}
                    placeholder={isViewMode ? "Sem data definida" : "Opcional"}
                    value={endDate}
                    onChange={(date) => setEndDate(date)}
                    disabled={isViewMode && !isEditMode}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item
                  label="Descrição"
                  name="description"
                  rules={[{ required: !isViewMode || isEditMode, message: "Por favor, insira a descrição." }]}
                >
                  <TextArea
                    rows={3}
                    placeholder="Descreva os benefícios e detalhes da assinatura"
                    onChange={(e) => setDescription(e.target.value)}
                    value={description}
                    disabled={isViewMode && !isEditMode}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Content>

        {/* Section 2: Payment Methods */}
        <Content
          style={{
            padding: 24,
            margin: "30px 0 0 0",
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <div className="container-subtitle">
            <h3 className="subtitle-page">
              {isViewMode ? "2." : "2."} Métodos de Pagamento
            </h3>
          </div>
          <Divider />
          <Row gutter={24}>
            {paymentMethodOptions.map((method) => (
              <Col xs={24} md={8} key={method.value}>
                <Card className={`payment-method-card ${isViewMode ? "view-mode" : ""}`}>
                  <div className="payment-method-header">
                    <Checkbox
                      checked={paymentMethods.includes(method.value)}
                      onChange={(e) =>
                        handlePaymentMethodChange(method.value, e.target.checked)
                      }
                      disabled={isViewMode && !isEditMode}
                    >
                      <span className="payment-method-label">{method.label}</span>
                    </Checkbox>
                  </div>
                  {paymentMethods.includes(method.value) && (
                    <div className="payment-method-options">
                      <Divider style={{ margin: "12px 0" }} />
                      <Checkbox
                        checked={paymentMethodsNoTax.includes(method.value)}
                        onChange={(e) =>
                          handlePaymentMethodNoTaxChange(method.value, e.target.checked)
                        }
                        disabled={isViewMode && !isEditMode}
                      >
                        <span className="no-tax-label">
                          Sem taxa para o cliente
                          {(!isViewMode || isEditMode) && (
                            <Tooltip title="Marque esta opção para que o cliente não pague taxas neste método">
                              <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                            </Tooltip>
                          )}
                        </span>
                      </Checkbox>
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </Content>

        {/* Section 3: Notifications */}
        <Content
          style={{
            padding: 24,
            margin: "30px 0 0 0",
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <div className="container-subtitle">
            <h3 className="subtitle-page">
              {isViewMode ? "3." : "3."} Configuração de Notificações
            </h3>
          </div>
          <Divider />
          <Row gutter={[24, 16]}>
            <Col xs={24} md={12}>
              <Card className="notification-card">
                <div className="notification-item">
                  <div className="notification-info">
                    <span className="notification-title">Notificar novo link de pagamento</span>
                    <span className="notification-description">
                      Enviar notificação quando um novo link de cobrança for gerado
                    </span>
                  </div>
                  <Switch
                    checked={notifyNewPaymentLink}
                    onChange={setNotifyNewPaymentLink}
                    disabled={isViewMode && !isEditMode}
                  />
                </div>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card className="notification-card">
                <div className="notification-item">
                  <div className="notification-info">
                    <span className="notification-title">Notificar no dia do vencimento</span>
                    <span className="notification-description">
                      Enviar lembrete no dia em que a cobrança vence
                    </span>
                  </div>
                  <Switch
                    checked={notifyOnDueDate}
                    onChange={setNotifyOnDueDate}
                    disabled={isViewMode && !isEditMode}
                  />
                </div>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card className="notification-card">
                <div className="notification-item">
                  <div className="notification-info">
                    <span className="notification-title">Notificar pagamento confirmado</span>
                    <span className="notification-description">
                      Enviar confirmação quando o pagamento for realizado
                    </span>
                  </div>
                  <Switch
                    checked={notifyPaymentConfirmed}
                    onChange={setNotifyPaymentConfirmed}
                    disabled={isViewMode && !isEditMode}
                  />
                </div>
              </Card>
            </Col>
          </Row>
          <Divider />
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <div className="notification-timing">
                <label>Dias antes do vencimento para notificar</label>
                <InputNumber
                  min={1}
                  max={30}
                  value={daysBeforeDue}
                  onChange={setDaysBeforeDue}
                  addonAfter="dias"
                  disabled={isViewMode && !isEditMode}
                />
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div className="notification-timing">
                <label>Dias após o vencimento para notificar atraso</label>
                <InputNumber
                  min={1}
                  max={30}
                  value={daysAfterOverdue}
                  onChange={setDaysAfterOverdue}
                  addonAfter="dias"
                  disabled={isViewMode && !isEditMode}
                />
              </div>
            </Col>
          </Row>
        </Content>

        <div className="container-buttons">
          <Button onClick={() => navigate("/assinaturas")}>
            {isViewMode && !isEditMode ? "Voltar" : "Cancelar"}
          </Button>
          {!isViewMode && (
            <Button type="primary" onClick={onFinish} loading={loading}>
              Criar Assinatura
            </Button>
          )}
          {isEditMode && (
            <Button type="primary" onClick={onFinish} loading={loading}>
              Salvar alterações
            </Button>
          )}
        </div>
      </Layout>
    </div>
  );
};

export default SubscriptionCreate;
